import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Save, Undo2, Redo2, Monitor, Tablet, Smartphone, Eye, Download,
  ArrowLeft, Layers, FolderTree, Sparkles, Code2, Palette, Type, Braces, Power,
  PanelLeft, PanelRight,
} from "lucide-react";
import { cloner, buildSrcDoc, cleanHtml, rawUrl, zipUrl, describe } from "@/lib/clonerApi";
import { apiError } from "@/lib/userApi";
import { StylePanel } from "@/components/cloner/StylePanel";
import { ContentPanel } from "@/components/cloner/ContentPanel";
import { CodePanel } from "@/components/cloner/CodePanel";
import { AiPanel } from "@/components/cloner/AiPanel";
import { FilesPanel, AskPanel } from "@/components/cloner/FilesPanel";
import { LayersPanel } from "@/components/cloner/LayersPanel";

const DEVICES = [["escritorio", "100%", Monitor], ["tablet", "820px", Tablet],
  ["movil", "390px", Smartphone]];

const Tab = ({ id, active, onClick, icon: Icon, label }) => (
  <button onClick={() => onClick(id)} data-testid={`tab-${id}`}
    className={`flex-1 px-2 py-2 text-[10px] flex flex-col items-center gap-0.5 transition-colors ${
      active ? "text-white bg-white/[0.07]" : "text-white/45 hover:text-white/80"}`}>
    <Icon className="w-3.5 h-3.5" /> {label}
  </button>
);

export default function ClonerEditor() {
  const { siteId } = useParams();
  const nav = useNavigate();
  const frame = useRef(null);
  const lastHtml = useRef("");
  const undoStack = useRef([]);
  const redoStack = useRef([]);

  const [site, setSite] = useState(null);
  const [files, setFiles] = useState([]);
  const [path, setPath] = useState("");
  const [srcDoc, setSrcDoc] = useState("");
  const [doc, setDoc] = useState(null);
  const [sel, setSel] = useState(null);
  const [version, setVersion] = useState(0);
  const [dirty, setDirty] = useState(false);
  const [jsOn, setJsOn] = useState(false);
  const [device, setDevice] = useState("escritorio");
  const [left, setLeft] = useState("capas");
  const [right, setRight] = useState("estilos");
  const [loading, setLoading] = useState(true);
  const [frameKey, setFrameKey] = useState(0);
  const [showLeft, setShowLeft] = useState(() => window.innerWidth >= 1024);
  const [showRight, setShowRight] = useState(() => window.innerWidth >= 1024);

  const pages = files.filter((f) => f.kind === "pagina");

  /* ------------------------------------------------------------- carga */
  const loadAll = useCallback(async () => {
    try {
      const [{ data: s }, { data: f }] = await Promise.all([
        cloner.site(siteId), cloner.files(siteId),
      ]);
      setSite(s);
      setFiles(f.files);
      return f.files;
    } catch (e) {
      toast.error(apiError(e, "No se pudo abrir el sitio"));
      nav("/clonador");
      return [];
    } finally { setLoading(false); }
  }, [siteId, nav]);

  const render = useCallback((html, p = path, js = jsOn) => {
    lastHtml.current = html;
    setSel(null);
    setSrcDoc(buildSrcDoc(html, { siteId, path: p, js }));
    setFrameKey((k) => k + 1);
  }, [siteId, path, jsOn]);

  const openPage = useCallback(async (p) => {
    try {
      const { data } = await cloner.text(siteId, p);
      setPath(p);
      undoStack.current = [];
      redoStack.current = [];
      setDirty(false);
      lastHtml.current = data.content;
      setSel(null);
      setSrcDoc(buildSrcDoc(data.content, { siteId, path: p, js: jsOn }));
    } catch (e) { toast.error(apiError(e, "No se pudo abrir la página")); }
  }, [siteId, jsOn]);

  useEffect(() => {
    (async () => {
      const f = await loadAll();
      const first = f.find((x) => x.path === "index.html") || f.find((x) => x.kind === "pagina");
      if (first) openPage(first.path);
    })();
    // eslint-disable-next-line
  }, [siteId]);

  /* --------------------------------------------------------- selección */
  const getDoc = () => {
    try { return frame.current?.contentDocument || null; } catch { return null; }
  };

  const touch = useCallback(() => {
    const d = getDoc();
    if (!d) return;
    const html = cleanHtml(d);
    if (html === lastHtml.current) return;
    undoStack.current = [...undoStack.current.slice(-29), lastHtml.current];
    redoStack.current = [];
    lastHtml.current = html;
    setDirty(true);
    setVersion((v) => v + 1);
  }, []);

  const pick = useCallback((el) => {
    const d = getDoc();
    if (!d || !el) return;
    d.querySelectorAll("[data-fx-sel]").forEach((n) => n.removeAttribute("data-fx-sel"));
    el.setAttribute("data-fx-sel", "1");
    setSel(el);
    setVersion((v) => v + 1);
  }, []);

  const onFrameLoad = () => {
    let d = null;
    try { d = frame.current?.contentDocument; } catch { d = null; }
    if (!d) { setDoc(null); return; }   // modo vista previa con JS: sin edición
    setDoc(d);
    setVersion((v) => v + 1);
    d.addEventListener("mouseover", (e) => {
      d.querySelectorAll("[data-fx-hover]").forEach((n) => n.removeAttribute("data-fx-hover"));
      if (e.target !== d.body) e.target.setAttribute?.("data-fx-hover", "1");
    });
    d.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      pick(e.target);
    }, true);
    d.addEventListener("dblclick", (e) => {
      e.preventDefault();
      e.target.setAttribute("contenteditable", "true");
      e.target.focus();
    }, true);
    d.addEventListener("input", () => setDirty(true));
    d.addEventListener("keydown", (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        save();
      }
    });
  };

  /* ------------------------------------------------------------ acciones */
  const save = async () => {
    const d = getDoc();
    if (!path) return;
    if (!d) return toast.error("Apaga el JS (botón JS ON) para poder editar y guardar");
    try {
      const html = cleanHtml(d);
      await cloner.save(siteId, path, html);
      lastHtml.current = html;
      setDirty(false);
      toast.success("Página guardada");
    } catch (e) { toast.error(apiError(e, "No se pudo guardar")); }
  };

  const undo = () => {
    if (!undoStack.current.length) return;
    const prev = undoStack.current.pop();
    redoStack.current = [...redoStack.current, lastHtml.current];
    render(prev);
  };
  const redo = () => {
    if (!redoStack.current.length) return;
    const next = redoStack.current.pop();
    undoStack.current = [...undoStack.current, lastHtml.current];
    render(next);
  };

  const applyElementHtml = (html, onlyRefresh) => {
    if (onlyRefresh) return touch();
    if (!sel || !html) return;
    sel.outerHTML = html;
    setSel(null);
    touch();
  };

  const applyPageHtml = (html) => {
    undoStack.current = [...undoStack.current, lastHtml.current];
    redoStack.current = [];
    setDirty(true);
    render(html);
  };

  const onAi = (code, scope) => {
    if (scope === "elemento" && sel) {
      sel.outerHTML = code;
      setSel(null);
      touch();
    } else {
      applyPageHtml(code);
    }
  };

  const toggleJs = () => {
    const next = !jsOn;
    setJsOn(next);
    const d = getDoc();
    render(d ? cleanHtml(d) : lastHtml.current, path, next);
    if (next) toast.info("Vista previa con JavaScript: para editar, apágalo otra vez");
  };

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") { e.preventDefault(); save(); }
      else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") { e.preventDefault(); undo(); }
      else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "y") { e.preventDefault(); redo(); }
      else if (e.key === "Escape") setSel(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line
  }, [path, sel]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#05060a" }}>
      <Loader2 className="w-6 h-6 animate-spin text-[#FF8A2B]" /></div>;
  }

  const width = DEVICES.find((d) => d[0] === device)[1];

  return (
    <div className="h-screen flex flex-col theme-noir" style={{ backgroundColor: "#05060a" }}
      data-testid="cloner-editor">
      {/* barra superior */}
      <div className="h-14 shrink-0 flex items-center gap-2 px-3 border-b border-white/8 backdrop-blur-xl
                      overflow-x-auto whitespace-nowrap"
        style={{ backgroundColor: "rgba(10,14,22,0.8)" }}>
        <button onClick={() => nav("/clonador")} data-testid="editor-back"
          className="glass-btn px-2.5 py-2 rounded-xl text-xs"><ArrowLeft className="w-3.5 h-3.5" /></button>
        <button onClick={() => setShowLeft((v) => !v)} data-testid="editor-toggle-left"
          className="glass-btn px-2.5 py-2 rounded-xl text-xs lg:hidden">
          <PanelLeft className="w-3.5 h-3.5" /></button>
        <button onClick={() => setShowRight((v) => !v)} data-testid="editor-toggle-right"
          className="glass-btn px-2.5 py-2 rounded-xl text-xs lg:hidden">
          <PanelRight className="w-3.5 h-3.5" /></button>
        <input value={site?.name || ""} data-testid="editor-name"
          onChange={(e) => setSite({ ...site, name: e.target.value })}
          onBlur={() => cloner.rename(siteId, site.name)}
          className="w-40 glass-card px-2 py-1.5 bg-black/40 text-white text-xs outline-none" />
        <select value={path} onChange={(e) => openPage(e.target.value)} data-testid="editor-page-select"
          className="glass-card px-2 py-1.5 bg-black/60 text-white text-[11px] outline-none max-w-[220px]">
          {pages.map((p) => (
            <option key={p.path} value={p.path} className="bg-black">{p.path}</option>
          ))}
        </select>
        <button onClick={toggleJs} data-testid="editor-js-toggle"
          className={`glass-btn px-2.5 py-2 rounded-xl text-[10px] flex items-center gap-1 ${
            jsOn ? "text-emerald-300" : "text-white/50"}`}>
          <Power className="w-3.5 h-3.5" /> JS {jsOn ? "ON" : "OFF"}
        </button>
        <button onClick={undo} data-testid="editor-undo"
          className="glass-btn px-2.5 py-2 rounded-xl text-xs"><Undo2 className="w-3.5 h-3.5" /></button>
        <button onClick={redo} data-testid="editor-redo"
          className="glass-btn px-2.5 py-2 rounded-xl text-xs"><Redo2 className="w-3.5 h-3.5" /></button>
        <div className="flex items-center gap-1 ml-1">
          {DEVICES.map(([id, , Icon]) => (
            <button key={id} onClick={() => setDevice(id)} data-testid={`editor-device-${id}`}
              className={`glass-btn px-2.5 py-2 rounded-xl ${device === id ? "glass-btn-red" : ""}`}>
              <Icon className="w-3.5 h-3.5" />
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <span className="text-[10px] text-white/35 hidden sm:block">
          {sel ? describe(sel) : "nada elegido"}
        </span>
        <a href={rawUrl(siteId, path)} target="_blank" rel="noreferrer" data-testid="editor-preview"
          className="glass-btn px-2.5 py-2 rounded-xl text-xs"><Eye className="w-3.5 h-3.5" /></a>
        <a href={zipUrl(siteId)} data-testid="editor-zip"
          className="glass-btn px-2.5 py-2 rounded-xl text-xs"><Download className="w-3.5 h-3.5" /></a>
        <button onClick={save} data-testid="editor-save"
          className={`glass-btn ${dirty ? "glass-btn-red" : ""} px-4 py-2 rounded-xl text-xs flex items-center gap-1.5`}>
          <Save className="w-3.5 h-3.5" /> Guardar
        </button>
      </div>

      <div className="flex-1 flex min-h-0 relative">
        {/* panel izquierdo */}
        <aside className={`${showLeft ? "flex" : "hidden"} lg:flex w-60 shrink-0 border-r border-white/8 flex-col min-h-0
                           absolute lg:static inset-y-0 left-0 z-20 lg:z-auto backdrop-blur-xl`}
          style={{ backgroundColor: "rgba(10,14,22,0.92)" }}>
          <div className="flex border-b border-white/8">
            <Tab id="capas" active={left === "capas"} onClick={setLeft} icon={Layers} label="Capas" />
            <Tab id="archivos" active={left === "archivos"} onClick={setLeft} icon={FolderTree} label="Archivos" />
            <Tab id="asistente" active={left === "asistente"} onClick={setLeft} icon={Sparkles} label="Asistente" />
          </div>
          <div className="flex-1 overflow-y-auto">
            {left === "capas" && (
              <LayersPanel doc={doc} selected={sel} onPick={pick} version={version} />
            )}
            {left === "archivos" && (
              <FilesPanel siteId={siteId} files={files} onOpenPage={openPage} />
            )}
            {left === "asistente" && (
              <AskPanel siteId={siteId} onOpenPath={(p) => {
                if (p.endsWith(".html")) openPage(p);
                else { setLeft("archivos"); toast.info(`Busca ${p} en Archivos`); }
              }} />
            )}
          </div>
        </aside>

        {/* lienzo */}
        <main className="flex-1 min-w-0 overflow-auto flex justify-center p-4 relative"
          style={{ background: "repeating-conic-gradient(#0a0c12 0% 25%, #0d1017 0% 50%) 50%/22px 22px" }}>
          {jsOn && (
            <div className="absolute top-2 left-1/2 -translate-x-1/2 z-10 glass-card px-3 py-1.5
                            text-[10px] text-amber-200" data-testid="editor-preview-notice">
              Vista previa con JavaScript · apaga «JS ON» para editar
            </div>
          )}
          <iframe ref={frame} key={frameKey} srcDoc={srcDoc} onLoad={onFrameLoad} title="vista"
            data-testid="editor-frame"
            sandbox={jsOn ? "allow-scripts allow-forms allow-popups"
              : "allow-same-origin"}
            className="bg-white rounded-lg shadow-2xl"
            style={{ width, height: "100%", minHeight: "100%", border: "none" }} />
        </main>

        {/* panel derecho */}
        <aside className={`${showRight ? "flex" : "hidden"} lg:flex w-72 shrink-0 border-l border-white/8 flex-col min-h-0
                           absolute lg:static inset-y-0 right-0 z-20 lg:z-auto backdrop-blur-xl`}
          style={{ backgroundColor: "rgba(10,14,22,0.92)" }}>
          <div className="flex border-b border-white/8">
            <Tab id="estilos" active={right === "estilos"} onClick={setRight} icon={Palette} label="Estilos" />
            <Tab id="contenido" active={right === "contenido"} onClick={setRight} icon={Type} label="Contenido" />
            <Tab id="codigo" active={right === "codigo"} onClick={setRight} icon={Braces} label="Código" />
            <Tab id="ia" active={right === "ia"} onClick={setRight} icon={Sparkles} label="IA" />
          </div>
          <div className="flex-1 overflow-y-auto">
            {right === "estilos" && <StylePanel el={sel} onChange={touch} version={version} />}
            {right === "contenido" && (
              <ContentPanel el={sel} siteId={siteId} path={path} onChange={touch} />
            )}
            {right === "codigo" && (
              <CodePanel el={sel} doc={doc} version={version}
                onElementHtml={applyElementHtml} onPageHtml={applyPageHtml} />
            )}
            {right === "ia" && (
              <AiPanel siteId={siteId} el={sel} path={path} onResult={onAi}
                getPageHtml={() => { const d = getDoc(); return d ? cleanHtml(d) : lastHtml.current; }} />
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
